import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface SearchProduct {
  id: string;
  name: string;
  category: string;
  price: number;
  original_price: number;
  thumbnail: string;
  images: string[];
  sizes: string[];
}

const POPULAR_SEARCHES = ['Track Pants', 'T-Shirts', 'Hoodies', 'Jeans', 'Shorts'];

export default function HomeSearchBar() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [allProducts, setAllProducts] = useState<SearchProduct[]>([]);
  const [suggestions, setSuggestions] = useState<SearchProduct[]>([]);
  const [noResults, setNoResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Fetch all approved products once
  useEffect(() => {
    supabase
      .from('products')
      .select('id, name, category, price, original_price, thumbnail, images, sizes')
      .eq('status', 'approved')
      .then(({ data }) => {
        if (data) setAllProducts(data as SearchProduct[]);
      });
  }, []);

  // Filter suggestions on query change
  useEffect(() => {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      setSuggestions([]);
      setNoResults(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => {
      const filtered = allProducts.filter(
        (p) =>
          p.name.toLowerCase().includes(trimmed) ||
          p.category.toLowerCase().includes(trimmed)
      );
      setSuggestions(filtered.slice(0, 6));
      setNoResults(filtered.length === 0);
      setLoading(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [query, allProducts]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const goToCategory = useCallback(
    (category: string, searchQuery?: string) => {
      setIsFocused(false);
      setQuery('');
      const url = searchQuery
        ? `/category?cat=${encodeURIComponent(category)}&search=${encodeURIComponent(searchQuery)}`
        : `/category?cat=${encodeURIComponent(category)}`;
      navigate(url);
    },
    [navigate]
  );

  const handleSuggestionClick = (product: SearchProduct) => {
    goToCategory(product.category, product.name);
  };

  const handleSearch = () => {
    if (!query.trim()) return;
    // Find best matching category
    const trimmed = query.trim().toLowerCase();
    const match = allProducts.find(
      (p) =>
        p.name.toLowerCase().includes(trimmed) ||
        p.category.toLowerCase().includes(trimmed)
    );
    if (match) {
      goToCategory(match.category, query.trim());
    } else {
      // Navigate to Track Pants with query so user sees no results message
      navigate(`/category?cat=Track%20Pants&search=${encodeURIComponent(query.trim())}`);
      setIsFocused(false);
    }
  };

  const showDropdown = isFocused && query.trim().length > 0;

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Search Input */}
      <div
        className={`flex items-center gap-2.5 bg-white rounded-2xl border-2 px-4 py-3 transition-all duration-200 ${
          isFocused
            ? 'border-orange-400 shadow-lg shadow-orange-100'
            : 'border-gray-200 shadow-sm'
        }`}
      >
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          {loading ? (
            <i className="ri-loader-4-line animate-spin text-orange-400 text-base"></i>
          ) : (
            <i className={`ri-search-line text-base ${isFocused ? 'text-orange-500' : 'text-gray-400'}`}></i>
          )}
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="Search products, categories..."
          className="flex-1 bg-transparent outline-none text-sm text-gray-800 placeholder-gray-400 min-w-0"
        />

        {query ? (
          <button
            onClick={() => { setQuery(''); setNoResults(false); inputRef.current?.focus(); }}
            className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-xs text-gray-600"></i>
          </button>
        ) : (
          <button
            onClick={handleSearch}
            className="shrink-0 px-3 py-1.5 rounded-xl bg-orange-500 text-white text-xs font-bold cursor-pointer hover:bg-orange-600 transition-colors whitespace-nowrap"
          >
            Search
          </button>
        )}
      </div>

      {/* Popular search chips — shown when not typing */}
      {!query && !isFocused && (
        <div className="flex gap-2 mt-2.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
          {POPULAR_SEARCHES.map((cat) => (
            <button
              key={cat}
              onClick={() => goToCategory(cat)}
              className="shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200 border border-transparent transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-fire-line text-[10px] text-orange-400"></i>
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl border border-gray-200 shadow-2xl z-50 overflow-hidden">

          {/* Suggestions list */}
          {suggestions.length > 0 && (
            <>
              <div className="px-4 pt-3 pb-1">
                <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
                  Products found
                </p>
              </div>
              {suggestions.map((product) => {
                const img = product.thumbnail || product.images?.[0] || '';
                const discount = product.original_price > product.price
                  ? Math.round((1 - product.price / product.original_price) * 100)
                  : 0;
                return (
                  <button
                    key={product.id}
                    onMouseDown={() => handleSuggestionClick(product)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-orange-50 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-10 h-12 rounded-xl overflow-hidden bg-gray-100 shrink-0 border border-gray-100">
                      {img ? (
                        <img src={img} alt={product.name} className="w-full h-full object-cover object-top" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-t-shirt-line text-gray-300 text-lg"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-semibold text-xs leading-tight truncate">{product.name}</p>
                      <p className="text-gray-400 text-[10px] mt-0.5">{product.category}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-orange-600 font-bold text-xs">₹{product.price}</span>
                        {discount > 0 && (
                          <span className="text-green-600 text-[9px] font-bold bg-green-50 px-1 rounded">
                            {discount}% off
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      <div className="flex gap-0.5 flex-wrap justify-end max-w-[56px]">
                        {product.sizes?.slice(0, 3).map((s) => (
                          <span key={s} className="text-[9px] text-gray-400 bg-gray-100 px-1 rounded">
                            {s}
                          </span>
                        ))}
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-300 text-sm"></i>
                    </div>
                  </button>
                );
              })}
            </>
          )}

          {/* No results — show suggested products */}
          {noResults && (
            <div className="px-4 py-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <i className="ri-search-line text-orange-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-gray-800 font-bold text-sm">No results for &quot;{query}&quot;</p>
                  <p className="text-gray-400 text-xs">Try these popular categories:</p>
                </div>
              </div>
              {/* Suggested categories */}
              <div className="grid grid-cols-2 gap-2">
                {POPULAR_SEARCHES.map((cat) => (
                  <button
                    key={cat}
                    onMouseDown={() => goToCategory(cat)}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-100 hover:bg-orange-50 hover:border-orange-200 transition-all cursor-pointer text-left"
                  >
                    <i className="ri-t-shirt-line text-orange-400 text-sm shrink-0"></i>
                    <span className="text-gray-700 font-semibold text-xs truncate">{cat}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          {suggestions.length > 0 && (
            <div className="px-4 py-2.5 border-t border-gray-100">
              <button
                onMouseDown={handleSearch}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 cursor-pointer py-1"
              >
                <i className="ri-search-line text-xs"></i>
                See all results for &quot;{query}&quot;
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
